import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { getSelectedCompany, resolveCompany, cacheDynamicCompanies } from "../companies";
import { User, Lock, Eye, EyeOff, ShieldCheck, AlertCircle, ArrowLeft, CheckCircle2, Mail } from "lucide-react";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const companyCode = getSelectedCompany();
  const [company, setCompany] = useState(resolveCompany(companyCode));

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [forgot, setForgot] = useState(false);
  const [resetId, setResetId] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [resetErr, setResetErr] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (!companyCode) { navigate("/seleziona-societa", { replace: true }); return; }
    if (!company) {
      // Tenant dinamico non ancora in cache: recuperalo dall'endpoint pubblico.
      api.get("/companies/public").then((r) => {
        const list = r.data?.companies || [];
        cacheDynamicCompanies(list);
        const resolved = resolveCompany(companyCode);
        if (resolved) setCompany(resolved);
        else navigate("/seleziona-societa", { replace: true });
      }).catch(() => navigate("/seleziona-societa", { replace: true }));
    }
  }, [companyCode, company, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(username, password, companyCode, remember);
    setSubmitting(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setResetErr("");
    setResetMsg("");
    if (!resetId.trim()) { setResetErr("Inserisci matricola o email aziendale"); return; }
    setResetLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { identifier: resetId.trim(), company: companyCode });
      setResetMsg(r.data?.message || "Se l'utenza esiste, riceverai a breve un'email con una password temporanea.");
    } catch {
      setResetMsg("Se l'utenza esiste, riceverai a breve un'email con una password temporanea.");
    } finally { setResetLoading(false); }
  };

  const openForgot = () => { setForgot(true); setResetId(username); setResetMsg(""); setResetErr(""); };
  const closeForgot = () => { setForgot(false); setResetMsg(""); setResetErr(""); };

  if (!company) return null;
  const accent = company.accent;

  return (
    <div className="min-h-screen flex bg-[#f0f2f5]">
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#16345c] to-[#0d1b2f] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1000&q=80"
          alt="Sede del gruppo Teleborsa"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center justify-between">
            <div className="bg-white rounded-xl px-5 py-3.5 shadow-sm inline-flex items-center">
              {company.logo
                ? <img src={company.logo} alt={company.label} className="h-9 max-w-[190px] object-contain" data-testid="login-company-logo" />
                : <span data-testid="login-company-logo" className="text-[20px] font-black" style={{ color: accent }}>{company.label}</span>}
            </div>
            <button
              onClick={() => navigate("/seleziona-societa")}
              data-testid="login-change-company"
              className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft size={14} /> Cambia società
            </button>
          </div>

          <div>
            <h1 className="text-[40px] font-black leading-tight mb-4">Il tuo spazio<br />di lavoro digitale</h1>
            <p className="text-[16px] text-gray-300 max-w-md leading-relaxed">
              Piattaforma dedicata ai dipendenti di <span className="font-semibold" style={{ color: accent }}>{company.label}</span>, azienda del gruppo Teleborsa. Accedi a buste paga, ferie, comunicazioni aziendali e alle app ufficiali.
            </p>
          </div>

          <div className="flex items-center gap-2 text-gray-400 text-[13px]">
            <ShieldCheck size={16} /> Accesso riservato e protetto
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden text-center mb-6">
            {company.logo
              ? <img src={company.logo} alt={company.label} className="h-10 max-w-[220px] object-contain mx-auto" />
              : <span className="text-[22px] font-black" style={{ color: accent }}>{company.label}</span>}
            <div className="text-[12px] text-gray-500 uppercase tracking-widest mt-2">Portale HR</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
            <div className="w-12 h-1.5 rounded-full mb-5" style={{ backgroundColor: accent }} />

            {forgot ? (
              <div data-testid="forgot-panel">
                <h2 className="text-[22px] font-black text-[#16345c] mb-1">Recupera password</h2>
                <p className="text-[13px] text-gray-500 mb-6">Inserisci la tua matricola o email aziendale: riceverai una password temporanea via email.</p>

                {resetMsg ? (
                  <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[13px] px-3 py-3 rounded-sm mb-4" data-testid="forgot-success">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> <span>{resetMsg}</span>
                  </div>
                ) : (
                  <>
                    {resetErr && (
                      <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-[#c00510] text-[13px] px-3 py-2 rounded-sm mb-4" data-testid="forgot-error">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{resetErr}</span>
                      </div>
                    )}
                    <form onSubmit={handleForgot} className="space-y-4">
                      <div>
                        <label className="block text-[12px] font-semibold text-gray-600 mb-1">Matricola / Email aziendale</label>
                        <div className="flex items-center border border-gray-300 rounded-sm focus-within:border-[#16345c] transition-colors">
                          <span className="px-3 text-gray-400"><Mail size={17} /></span>
                          <input
                            value={resetId}
                            onChange={(e) => setResetId(e.target.value)}
                            type="text"
                            data-testid="forgot-identifier"
                            placeholder="Matricola o email"
                            className="flex-1 py-2.5 pr-3 text-[14px] outline-none"
                          />
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={resetLoading}
                        data-testid="forgot-submit"
                        style={{ backgroundColor: accent }}
                        className="w-full hover:opacity-90 transition-opacity text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide disabled:opacity-60"
                      >
                        {resetLoading ? "Invio in corso..." : "Invia password temporanea"}
                      </button>
                    </form>
                  </>
                )}

                <button
                  onClick={closeForgot}
                  data-testid="forgot-back"
                  className="mt-5 pt-4 border-t border-gray-100 w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold text-gray-500 hover:text-[#16345c] transition-colors"
                >
                  <ArrowLeft size={14} /> Torna al login
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-[22px] font-black text-[#16345c] mb-1">Accedi al portale</h2>
                <p className="text-[13px] text-gray-500 mb-6">Riservato ai dipendenti di <b>{company.label}</b></p>

                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-[#c00510] text-[13px] px-3 py-2 rounded-sm mb-4" data-testid="login-error">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Matricola / Email aziendale</label>
                    <div className="flex items-center border border-gray-300 rounded-sm focus-within:border-[#16345c] transition-colors">
                      <span className="px-3 text-gray-400"><User size={17} /></span>
                      <input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        type="text"
                        data-testid="login-username"
                        placeholder="Matricola o email"
                        className="flex-1 py-2.5 pr-3 text-[14px] outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Password</label>
                    <div className="flex items-center border border-gray-300 rounded-sm focus-within:border-[#16345c] transition-colors">
                      <span className="px-3 text-gray-400"><Lock size={17} /></span>
                      <input
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type={showPwd ? "text" : "password"}
                        data-testid="login-password"
                        placeholder="Inserisci la password"
                        className="flex-1 py-2.5 text-[14px] outline-none"
                      />
                      <button type="button" onClick={() => setShowPwd(!showPwd)} className="px-3 text-gray-400 hover:text-gray-600">
                        {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[12px]">
                    <label className="flex items-center gap-1.5 text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember" className="accent-[#16345c]" /> Ricordami
                    </label>
                    <button type="button" onClick={openForgot} data-testid="forgot-password-link" className="text-[#0d6cb8] font-semibold hover:underline">Password dimenticata?</button>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="login-submit"
                    style={{ backgroundColor: accent }}
                    className="w-full hover:opacity-90 transition-opacity text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide disabled:opacity-60"
                  >
                    {submitting ? "Accesso in corso..." : "Accedi"}
                  </button>
                </form>

                <button
                  onClick={() => navigate("/seleziona-societa")}
                  data-testid="login-change-company-bottom"
                  className="mt-5 pt-4 border-t border-gray-100 w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold text-gray-500 hover:text-[#16345c] transition-colors"
                >
                  <ArrowLeft size={14} /> Torna alla selezione società
                </button>
              </>
            )}
          </div>

          <p className="text-center text-[11px] text-gray-400 mt-4">© 2026 Gruppo Teleborsa - Portale HR</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
